export const MAX_INPUT_CHARS = 75000;

export type TranslationStatus = "pending" | "completed" | "failed";

export type GlobalStatus =
  | "pending"
  | "completed"
  | "partly_completed"
  | "failed";

export interface TextModeOption {
  value: string;
  label: string;
  description: string;
}

export const TEXT_MODES: TextModeOption[] = [
  {
    value: "forward",
    label: "Standard",
    description: "Direct translation from source to target.",
  },
  {
    value: "backward",
    label: "Back-check",
    description: "Translate and verify by translating back.",
  },
  {
    value: "vtt_translation",
    label: "Subtitles (VTT)",
    description: "Preserve VTT subtitle structure and timing.",
  },
  {
    value: "missing_spaces_translation",
    label: "Compact text",
    description: "Handle CamelCase or concatenated source text.",
  },
];

/**
 * Model options for single (text) translation.
 *
 * "all" is a sentinel meaning "run all models and return the best" — this is
 * the backend default when no llm_model is sent. The remaining values map to
 * the backend's SingleTranslationModelEnum.
 */
export const ALL_MODELS_VALUE = "all";

export const SINGLE_MODEL_OPTIONS = [
  { value: ALL_MODELS_VALUE, label: "All models (best)" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-5.4", label: "GPT-5.4" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "claude-sonnet-4", label: "Claude Sonnet 4" },
] as const;

export interface StartTranslationRequest {
  source_lang: string;
  target_lang: string[];
  text: string;
  llm_mode?: string;
  llm_model?: string[];
}

export interface StartTranslationResponse {
  status: string;
  id: string;
  description?: string | null;
}

/** A single cost record from GET /v1/costs. Amounts are in USD. */
export interface CostRecord {
  job_id: string;
  language: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  text_count: number;
  input_cost_usd: number;
  output_cost_usd: number;
  total_cost_usd: number;
  api_key_masked: string;
  timestamp: string;
  date: string;
}

export interface CostRecordsResponse {
  records: CostRecord[];
  count: number;
  filters_applied: Record<string, string | null>;
}

export interface LanguageTranslation {
  status: TranslationStatus | string;
  text?: string | null;
  best_models?: string | null;
  all_texts?: Record<string, unknown> | null;
  error?: string;
}

export interface GetTranslationResponse {
  global_status: GlobalStatus | string;
  id: string;
  translations?: Record<string, LanguageTranslation> | null;
}

export interface SubmitFeedbackRequest {
  translation_id: string;
  target_lang: string;
  feedback: string;
}

export interface HistoryEntry {
  id: string;
  sourceLang: string;
  targetLangs: string[];
  mode: string;
  preview: string;
  createdAt: string;
  /** Content hash identifying the entry server-side; used for deletion. */
  contentHash?: string;
}

/** Raw shape returned by the backend GET /v1/translations endpoint. */
export interface TranslationHistoryItem {
  id: string;
  content_hash: string;
  type?: "single" | "batch";
  source_lang: string;
  target_langs: string[];
  mode: string;
  preview: string;
  created_at: string;
  updated_at: string;
  file_name?: string | null;
  model?: string | null;
}

export interface ListTranslationsResponse {
  items: TranslationHistoryItem[];
  count: number;
}

export const isTerminalStatus = (status: string): boolean =>
  status === "completed" ||
  status === "partly_completed" ||
  status === "failed";
